import { Fragment } from "react";
import {
  MediaMessage,
  SmsMessage,
  Variable,
  VoicemailMessage,
} from "../Message";
import useMoment from "../../hooks/use-moment";
import { MediaMimeType } from "./types";
import { DeliveryStrategy } from "../../api/campaigns";

type MessageTemplate = {
  body: string;
  // Accepts either deliverAt or deliverAtTime for now because of campaign editor
  deliverAt?: Date | null;
  deliverAtTime?: string | null;
  voicemailId?: string | null;
  imageType?: MediaMimeType | null;
  imageUrl?: string | null;
  deliveryStrategy: DeliveryStrategy;
  delayInDays: number;
  delayInMinutes: number;
  daysBeforeEvent: number | null;
  daysAfterEvent: number | null;
  minutesBeforeEvent: number | null;
  minutesAfterEvent: number | null;
};

export default function MessageTemplatePreview({
  messageTemplate,
}: {
  messageTemplate: MessageTemplate;
}) {
  const moment = useMoment();
  if (
    !messageTemplate.body &&
    !messageTemplate.voicemailId &&
    !messageTemplate.imageUrl
  )
    return null;

  const metadata = (
    {
      immediate: () => `immediately`,
      days_before_event: () =>
        `${messageTemplate.daysBeforeEvent} day${
          messageTemplate.daysBeforeEvent !== 1 ? "s" : ""
        } before event at ${moment(
          messageTemplate.deliverAtTime ?? messageTemplate.deliverAt
        )
          .utc()
          .format("LT")}`,
      minutes_before_event: () =>
        `${messageTemplate.minutesBeforeEvent} minute${
          messageTemplate.minutesBeforeEvent !== 1 ? "s" : ""
        } before event`,
      days_after_event: () =>
        `${messageTemplate.daysAfterEvent} day${
          messageTemplate.daysAfterEvent !== 1 ? "s" : ""
        } after event at ${moment(
          messageTemplate.deliverAtTime ?? messageTemplate.deliverAt
        )
          .utc()
          .format("LT")}`,
      minutes_after_event: () =>
        `${messageTemplate.minutesAfterEvent} minute${
          messageTemplate.minutesAfterEvent !== 1 ? "s" : ""
        } after event`,
      delay_in_minutes: () =>
        `After ${messageTemplate.delayInMinutes} minute${
          messageTemplate.delayInMinutes !== 1 ? "s" : ""
        }`,
      delay_in_days: () =>
        `After ${messageTemplate.delayInDays} day${
          messageTemplate.delayInDays !== 1 ? "s" : ""
        } at ${moment(
          messageTemplate.deliverAtTime ?? messageTemplate.deliverAt
        )
          .utc()
          .format("LT")}`,
    }[messageTemplate.deliveryStrategy] || (() => "")
  )();

  const formattedMessageBody = messageTemplate.body
    ? messageTemplate.body
        .split("\n")
        .map((line, index) => (
          <div key={index}>
            {line
              .split(/\[([^[]+)]/g)
              .map((str, index) =>
                index % 2 !== 0 ? (
                  <Variable key={index}>{str}</Variable>
                ) : (
                  <Fragment key={index}>{str}&nbsp;</Fragment>
                )
              )}
          </div>
        ))
    : "";

  if (messageTemplate.imageUrl && messageTemplate.imageType) {
    return (
      <>
        <MediaMessage
          mediaType={messageTemplate.imageType}
          mediaUrl={messageTemplate.imageUrl}
          mediaThumbnail={true}
          stackedWithNextMessage={!!messageTemplate.body}
        />
        {!!messageTemplate.body && (
          <SmsMessage stackedWithPrevMessage={true} metadata={metadata}>
            {formattedMessageBody}
          </SmsMessage>
        )}
      </>
    );
  } else if (messageTemplate.voicemailId) {
    return (
      <VoicemailMessage
        voicemailId={messageTemplate.voicemailId}
        metadata={metadata}
      />
    );
  } else {
    return (
      <SmsMessage metadata={metadata}>
        {!!messageTemplate.body && formattedMessageBody}
      </SmsMessage>
    );
  }
}
